import React, {useState, Fragment, memo, useMemo} from 'react'
import PropTypes from 'prop-types'
import { Col } from 'react-flexbox-grid'
import GGDropdown from '../shared/new_components/GGDropdown'
import styled from 'styled-components'
import { GolfHubScheduleItems } from '../directory_setup/components/schedules/GolfHubScheduleItems'
import { GolfHubSponsorItems, GolfHubAddSponsorsPopup } from '../directory_setup/components/sponsors/GolfHubSponsorItems'
import { GolfHubAddPhotoPopup } from '../directory_setup/components/banners/GolfHubAddPhotoPopup'
import ThumbnailsCrop, { ThumbnailsCropPopup } from '../directory_setup/components/thumbnails/ThumbnailsCrop'
import ThumbnailsUpload, {ThumbnailsUploadPopup} from '../directory_setup/components/thumbnails/ThumbnailsUpload'
import { getAuthenticityToken } from 'Shared/helpers'
import IframeContainerThumbnail from './IframeContainerThumbnail'
import _ from 'lodash'
import { GGChipGroup } from '../shared/new_components/GGChipGroup'
import { GGButton } from '../shared/new_components/GGButton'
import DraggableItems from '../directory_setup/components/banners/DraggableItems'
import CroppedImage from '../directory_setup/components/banners/CroppedImage'

// Chip labels are keyed per item type instead of interpolating a translated noun
// into an English prefix ("New " + label), which reads wrong in fr/jp word order
const TEMPLATE_FIELD_KEYS = {
  schedule: 'schedule',
  sponsor: 'sponsor',
  course_album: 'banner',
}

const StyledFormComponents = styled.div`
  .rmargin-top-15 {
    margin-top: -15px;
  }

  .pointer {
    cursor: pointer;
  }

  .fa-calendar {
    font-size: 10px;
  }

  .event-subtype {
    width: 250px;
    margin-bottom: 20px;
  }

  .banner-error {
    float: left;
    margin-top: -29px;
    display: inline-block;
  }

  .inline_element {
    display: inline-block;
  }
`

const GolfHubRoot = ({schedules, sponsors, courseAlbums, leagueId, setup, isCharityOuting, thumbnail, eventCardPreviewData, isGgGolfHub, twoStepFlow}) => {
  const [ newSchedules, setNewSchedules ] = useState([ {time: new Date(), activity: ''} ])
  const [ newSponsors, setNewSponsors ] = useState([])
  const [ newSponsorsUpdated, setNewSponsorsUpdated ] = useState({})
  const [ newBannersUpdated, setNewBannersUpdated ] = useState({})
  const [ sponsorModalOpen, setSponsorModalOpen ] = useState(false)
  const [ sponsorDeleted, setSponsorDeleted ] = useState([])
  const [ newCourseAlbums, setNewCourseAlbums ] = useState([])
  const [ courseAlbumModalOpen, setCourseAlbumModalOpen ] = useState(false)
  const [ courseAlbumDeleted, setCourseAlbumDeleted ] = useState([])
  const [ newThumbnail, setNewThumbnail ] = useState({ image: '', crop: { x: 0, y: 0 }})
  const [ thumbnailSetting, setThumbnailSetting ] = useState(setup.thumbnail_setting)

  const setNewSponsor = (records) => {
    setNewSponsors(records)

    window.glg.changeNewRecords('sponsor', records)

    const formData = new FormData
    records.forEach(sponsor => {
      formData.append('sponsor_urls[]', sponsor.url)
      formData.append('sponsor_photos[]', sponsor.imageFile)
    })

    fetch(`/leagues/${leagueId}/create_temp_sponsors`, { method: 'POST', body: formData, headers: { 'X-CSRF-Token': getAuthenticityToken() } } )
    .then((response) => {
      return response.json()
    })
    .then((data) => {
      window.glg.showNewRecords('sponsors', data.sponsor.photos)
      const newSponsors = {}
      data.sponsor.photos.map((sponsor, index) => { newSponsors[records[index]?.image] = sponsor })
      setNewSponsorsUpdated(newSponsors)
    })
  }

  const setNewCourseAlbum = (records) => {
    setNewCourseAlbums(records)
    window.glg.changeNewRecords('course_album', records)

    if (records.length === 1 && thumbnailSetting === 'crop') {
      setNewThumbnail({ image: '', crop: { x: 0, y: 0 }})
    }

    const formData = new FormData
    records.forEach(ca => {
      formData.append('course_album_photos_crop[]', JSON.stringify(ca.crop))
      formData.append('course_album_photos[]', ca.imageFile)
    })

    fetch(`/leagues/${leagueId}/create_temp_course_albums`, { method: 'POST', body: formData, headers: { 'X-CSRF-Token': getAuthenticityToken() } } )
    .then((response) => {
      return response.json()
    })
    .then((data) => {
      window.glg.showNewRecords('course_album', data.course_album.photos.map((courseImage) => (courseImage.course_url)))
      const newBanners = {}
      data.course_album.photos.map((banner, index) => { newBanners[records[index]?.image] = banner })
      setNewBannersUpdated(newBanners)
    })
  }

  const submitForm = () => {
    const form = document.querySelector('.edit_golfhub_data')
    const formData = new FormData(form)

    formData.append('event_description', window.glg.CKEDITOR.instances['event_description'].getData())

    if (newCourseAlbums.length > 0) {
      if (isGgGolfHub) { formData.append('course_album', 'new') }
      newCourseAlbums.forEach(i => {
        formData.append('course_album_photos_crop[]', JSON.stringify(i.crop))
        formData.append('course_album_photos[]', i.imageFile)
      })
    }

    if (newSponsors.length > 0) {
      newSponsors.forEach(i => {
        formData.append('sponsor_urls[]', i.url)
        formData.append('sponsor_photos[]', i.imageFile)
      })
    }

    if (newThumbnail.image !== '') {
      formData.append('thumbnail_photo_crop', JSON.stringify(newThumbnail.crop))
      formData.append('thumbnail_photo', newThumbnail.imageFile)
      formData.append('ggsd_thumbnail_setting', thumbnailSetting)
    } else {
      formData.append('ggsd_thumbnail_setting', thumbnailSetting)
      formData.append('banner_thumbnail_no_crop', true)
      if (isGgGolfHub) formData.append('thumbnail', 'crop')
    }

    formData.append('charity_outing', isCharityOuting)

    const saveAndPublishForm = document.querySelector('.edit_golfhub_data.save_and_publish')
    let saveRoute = `/leagues/${leagueId}/update_golfhub_data`
    if (saveAndPublishForm !== null) {
      saveRoute = `/leagues/${leagueId}/save_and_publish_to_directory`
    }
    
    fetch(saveRoute, {
      method: 'PATCH',
      body: formData,
      headers: {'X-CSRF-Token': getAuthenticityToken()}})
    .then((res) => {
      return res.text()
    })
    .then((res) => {
      eval(res)
      if (saveAndPublishForm !== null) {
        const saveAndPublishButton = document.querySelector('.save_and_publish_golfhub_data')
        saveAndPublishButton.disabled = false
        saveAndPublishButton.classList.remove('disabled')
        saveAndPublishButton.value = window.I18n.t('views.golfhub.save_and_publish')
        saveAndPublishForm.classList.remove('save_and_publish')
      } else {
        const saveButton = document.querySelector('.save_golfhub_data')
        saveButton.disabled = false
        saveButton.classList.remove('disabled')
        saveButton.value = window.I18n.t('add_members.save')
      }
      if (twoStepFlow) {
        window.location.href = `/leagues/${leagueId}/setup_directory_registration`
      }
      return false
    })
  }

  window.glg.submitRegistrationFormFromGolfhubPage = submitForm
  return (
      <StyledFormComponents>
      {!isGgGolfHub &&
        <>
          <TemplateFields
            label={window.I18n.t('views.golfhub.template_fields.schedule')}
            leftLabel={window.I18n.t('views.golfhub.template_fields.sponsorship_logistics')}
            items={schedules}
            defaultValue={setup.directory_schedule_id}
            inputName='schedule'
            newRecords={newSchedules}
            setNewRecords={setNewSchedules}
            timeFormat={setup.time_format}
            isGgGolfHub={isGgGolfHub}
          />
          <TemplateFields
            label={window.I18n.t('views.golfhub.template_fields.sponsor')}
            items={sponsors}
            defaultValue={setup.sponsor_template_id}
            inputName='sponsor'
            newRecords={newSponsors}
            setNewRecords={setNewSponsors}
            deleted={sponsorDeleted}
            setDeleted={setSponsorDeleted}
            setModalOpen={setSponsorModalOpen}
            newRecordsUpdated={newSponsorsUpdated}
            isGgGolfHub={isGgGolfHub}
          />
          <div className='dashed-line add-margin-top-20' />
        </>}
        <TemplateFields
          label={window.I18n.t('views.golfhub.template_fields.banner')}
          leftLabel={window.I18n.t('views.golfhub.template_fields.branding')}
          items={courseAlbums}
          defaultValue={setup.course_album_id}
          inputName='course_album'
          newRecords={newCourseAlbums}
          setNewRecords={setNewCourseAlbums}
          deleted={courseAlbumDeleted}
          setDeleted={setCourseAlbumDeleted}
          setModalOpen={setCourseAlbumModalOpen}
          newRecordsUpdated={newBannersUpdated}
          thumbnail={thumbnail}
          eventCardPreviewData={eventCardPreviewData}
          thumbnail_setting={setup.thumbnail_setting}
          leagueId={leagueId}
          mainSetNewThumbnail={setNewThumbnail}
          mainSetThumbnailSetting={setThumbnailSetting}
          isGgGolfHub={isGgGolfHub}
        />
        <GolfHubAddSponsorsPopup
          modalOpen={sponsorModalOpen}
          setModalOpen={setSponsorModalOpen}
          images={newSponsors}
          setImages={setNewSponsor}
        />
        <GolfHubAddPhotoPopup
          modalOpen={courseAlbumModalOpen}
          setModalOpen={setCourseAlbumModalOpen}
          images={newCourseAlbums}
          setImages={setNewCourseAlbum}
        />
      </StyledFormComponents>
  )
}

// eslint-disable-next-line react/display-name
const TemplateFields = memo((props) => {
  let settingValue = props.defaultValue ? 'template' : 'none'
  if (props.inputName === 'course_album' && settingValue === 'none') {
    settingValue = 'new'
  }
  const itemKey = TEMPLATE_FIELD_KEYS[props.inputName]
  const label = props.inputName === 'schedule'
    ? window.I18n.t('views.golfhub.add_time_slot')
    : window.I18n.t(`views.golfhub.template_fields.new_${itemKey}`)
  const templateChipOptions = [ {
    name: props.inputName,
    value: 'template',
    label: window.I18n.t('views.golfhub.template'),
  },
  {
    name: props.inputName,
    value: 'new',
    label: label,
  }]
  if (props.inputName !== 'course_album') {
    templateChipOptions.push({
      name: props.inputName,
      value: 'none',
      label: window.I18n.t(`views.golfhub.template_fields.no_${itemKey}`),
    })
  }

  const [ setting, setSetting ] = useState(settingValue)
  const onRegisteredItemSelected = (e) => {
    window.glg.changeSelected(props.inputName, e)

    if (props.inputName === 'course_album') {
      window.glg.userSelectedAlbum(e)
      handleCourseAlbumChangeForThumbnail(props.items.find(courseAlbum => courseAlbum.id === e))
    }
  }

  const [ defaultValue, setDefaultValue ] = useState(props.defaultValue)

  if (props.inputName === 'course_album') {
    window.glg.forceBannerUpdate = (id) => {
      setDefaultValue(id)
      window.glg.changeSelected(props.inputName, id)
    }
    window.glg.updateEventDates = (startDate, endDate) => {
      setPreviewData(prev => ({ ...prev, event_start_date: startDate, event_end_date: endDate }))
    }
  }

  const onRadioSelected = (e) => {
    if (e === 'new') {
      window.glg.changeNewRecords(props.inputName, props.newRecords)
    } else if (e === 'none') {
      window.glg.changeSelected(props.inputName, [])
    } else {
      if (props.inputName === 'course_album'){
        window.glg.changeSelected(props.inputName, selectedCourseAlbum.id || props.items[0].id)
      } else {
        window.glg.changeSelected(props.inputName, props.defaultValue || props.items[0].id)
      }
    }
  }

  const setNewRecords = (recordsOrUpdater) => {
    const records = typeof recordsOrUpdater === 'function' ? recordsOrUpdater(props.newRecords) : recordsOrUpdater
    props.setNewRecords(records)
    window.glg.changeNewRecords(props.inputName, records)

    if (props.inputName === 'course_album') {
      window.glg.showNewRecords('course_album', records.map((record) => { return props.newRecordsUpdated[record.image].course_url }))
    } else if (props.inputName === 'sponsor') {
      window.glg.showNewRecords('sponsors', records.map((record) => { return props.newRecordsUpdated[record.image] }))
    }
  }

  if (props.inputName === 'course_album') {
    window.glg.uploadedNewBanner = () => {
      return props.newRecords.length > 0
    }
  }

  const [ thumbnailCropModalOpen, setThumbnailCropModalOpen ] = useState(false)
  const [ thumbnailUploadModalOpen, setThumbnailUploadModalOpen ] = useState(false)
  const [ selectedCourseAlbum, setSelectedCourseAlbum ] = useState(props.items.find(courseAlbum => courseAlbum.id === props.defaultValue))
  const [ selectedThumbnail, setSelectedThumbnail ] = useState(props.thumbnail || selectedCourseAlbum)
  const [ previewData, setPreviewData ] = useState(props.eventCardPreviewData)
  const [ newThumbnail, setNewThumbnail ] = useState({ image: '', crop: { x: 0, y: 0 }})
  const [ thumbnailSetting, setThumbnailSetting ] = useState(props.thumbnail_setting)
  const [ loadingForNewBanner, setLoadingForNewBanner ] = useState(false)

  const handleCourseAlbumChangeForThumbnail = (newSelectedCourseAlbum) => {
    setSelectedCourseAlbum(newSelectedCourseAlbum)
    if (thumbnailSetting === 'crop' || (newThumbnail.image === '' && !props.thumbnail)) {
      setSelectedThumbnail(newSelectedCourseAlbum)
      setNewThumbnail({ image: '', crop: { x: 0, y: 0 }})
      props.mainSetNewThumbnail({ image: '', crop: { x: 0, y: 0 }})
    }
  }

  const handleChooseCrop = () => {
    setThumbnailSetting('crop')
    props.mainSetThumbnailSetting('crop')
    setSelectedThumbnail(selectedCourseAlbum)
    setNewThumbnail({ image: '', crop: { x: 0, y: 0 }})
    props.mainSetNewThumbnail({ image: '', crop: { x: 0, y: 0 }})
  }

  const setNewSelectedThumbnail = (record) => {
    setNewThumbnail(record)
    props.mainSetNewThumbnail(record)
    const formData = new FormData

    formData.append('thumbnail_photo_crop', JSON.stringify(record.crop))
    formData.append('thumbnail_photo', record.imageFile)

    setPreviewData({
      ...previewData,
      loading: true,
    })

    fetch(`/leagues/${props.leagueId}/create_temp_thumbnail`, { method: 'POST', body: formData, headers: { 'X-CSRF-Token': getAuthenticityToken() } } )
      .then((response) => {
        return response.json()
      })
      .then((data) => {
        const newThumbnail = data.thumbnail
        setSelectedThumbnail(newThumbnail)
        setPreviewData({
          ...previewData,
          loading: false,
        })
      })
  }

  if (props.inputName === 'course_album' && props.newRecords.length > 0) {
    if (props.newRecords.length !== Object.keys(props.newRecordsUpdated).length && !previewData.loading) {
      setLoadingForNewBanner(true)
      setPreviewData({
        ...previewData,
        loading: true,
      })
    } else if (props.newRecords.length === Object.keys(props.newRecordsUpdated).length && loadingForNewBanner) {
      setLoadingForNewBanner(false)
      setPreviewData({
        ...previewData,
        loading: false,
      })
      if (props.newRecords.length === 1 && thumbnailSetting === 'crop') {
        setSelectedThumbnail(selectedCourseAlbum)
        setNewThumbnail({ image: '', crop: { x: 0, y: 0 }})
      }
    }
  }

  const thumb = useMemo(() => {
    const isTemplateOrEmtpy = (setting === 'template' && !props.isGgGolfHub) || _.isEmpty(props.newRecordsUpdated)

    if (thumbnailSetting === 'upload') {
      return newThumbnail['image'] !== '' ? selectedThumbnail : props.thumbnail
    } else {
      return isTemplateOrEmtpy || newThumbnail['image'] !== '' || props.newRecords.length === 0 ?
        selectedThumbnail : { photos: [ props.newRecordsUpdated[props.newRecords[0].image] ] }
    }
  }, [ setting, thumbnailSetting, newThumbnail, selectedThumbnail, props.newRecordsUpdated, props.newRecords, props.isGgGolfHub ])

  const cropChipOptions = [
    {
      name: 'thumbnail',
      value: 'crop',
      label: window.I18n.t('views.golfhub.crop_from_banner'),
    },
    {
      name: 'thumbnail',
      value: 'upload',
      label: window.I18n.t('views.golfhub.upload_thumbnail'),
    },
  ]

  const handleOnChangeCropChips = (v) => {
    if (v === 'crop') {
      handleChooseCrop()
    } else {
      setThumbnailSetting('upload')
      props.mainSetThumbnailSetting('upload')
    }
  }

  const defaultHubBanner = <div className='default-hub-banner'>
    {props.newRecords.length > 0 && props.newRecords[0].crop && <CroppedImage
      img={props.newRecords[0].image}
      newWidth={props.newRecords[0].crop.width}
      newHeight={props.newRecords[0].crop.height}
      startX={props.newRecords[0].crop.x}
      startY={props.newRecords[0].crop.y}
    />}
    {props.newRecords.length === 0 && selectedCourseAlbum?.photos && <img src={selectedCourseAlbum.photos[0].course_url} />}
  </div>

  const nonGgGolfHubContent = <Col xs={12} sm={9} className="no-padding description no-margin-bottom">
    <Col xs={12} sm={3} md={7} lg={12} className="inline_container add-padding-top-5 add-padding-right-45 add-padding-left-8">
      <div className='text-ds-form-label add-margin-bottom-5'>
        {props.label}
      </div>
      <GGChipGroup
        name={props.inputName}
        options={templateChipOptions}
        value={setting}
        onChange={(v) => { setSetting(v); onRadioSelected(v) }} />
      {setting === 'template' && (
        <div className='add-margin-top-10'>
          <div className='text-ds-form-label add-margin-bottom-10'>
            {window.I18n.t('views.golfhub.templates')}
          </div>
          <GGDropdown
            name={'selected_' + props.inputName}
            value={props.inputName === 'course_album' ? (selectedCourseAlbum?.id || props.items[0]?.id) : defaultValue}
            options={props.items.map((i) => ({ label: i.name, value: i.id }))}
            onChange={onRegisteredItemSelected}
          />
          {defaultHubBanner}
        </div>
      )}
    </Col>
    <Col xs={12} className="description no-margin-bottom no-padding">
      {setting === 'new' && (
        <Col xs={12} className="inline_container add-padding-top-5 add-padding-left-8">
          {props.inputName === 'schedule' && (
            <GolfHubScheduleItems
              schedules={props.newRecords}
              setSchedules={setNewRecords}
              timeFormat={props.timeFormat.replace('A', 'aa')}
            />
          )}
          {props.inputName === 'sponsor' && (
            <GolfHubSponsorItems
              images={props.newRecords}
              setImages={setNewRecords}
              deleted={props.deleted}
              setDeleted={props.setDeleted}
              setModalOpen={props.setModalOpen}
            />
          )}
          {props.inputName === 'course_album' && (
            <div>
              <DraggableItems
                images={props.newRecords}
                setImages={setNewRecords}
                deleted={props.deleted}
                setDeleted={props.setDeleted}
                setModalOpen={props.setModalOpen}
              />
              <div className='inline_element'>
                <div className='banner-error hidden error_text'>At least one photo is required</div>
              </div>
            </div>
          )}
        </Col>
      )}
    </Col>
  </Col>

  const GgGolfHubContent = <Col xs={12} sm={9} className="no-padding add-padding-left-8 add-padding-right-45">
    <div className='banners-header'>
      <div className='text-ds-form-label'>
        {window.I18n.t('views.golfhub.template_fields.banner')}
      </div>
      <div className='add-padding-left-8'>
        <GGButton
          usePro
          icon='pen-o'
          variant='ghost'
          onClick={() => props.setModalOpen(true)}
        />
      </div>
    </div>
    {defaultHubBanner}
    {selectedCourseAlbum?.id &&
      <input type='hidden' name={'selected_' + props.inputName} value={selectedCourseAlbum.id} />}
  </Col>
  return (
    <Fragment>
      <div className="row">
        <Col xs={12} className="description no-margin-bottom no-padding add-margin-top-30">
          <Col xs={12} sm={3} md={3} lg={3} className="no-margin-bottom no-padding add-padding-top-5 add-padding-left-15 text-ds-form-label">
            {props.leftLabel}
          </Col>
          {props.isGgGolfHub ? GgGolfHubContent : nonGgGolfHubContent}
        </Col>
      </div>
      { props.inputName === 'course_album' &&
        <div className="row">
          <Col xs={12} className="description no-margin-bottom no-padding add-margin-top-30">
            <Col xs={12} sm={3} md={3} lg={3} className="no-margin-bottom no-padding add-padding-top-5 add-padding-left-15"/>
              <Col xs={12} sm={9} className="no-padding description no-margin-bottom">
                <Col xs={12} className="description no-margin-bottom no-padding">
                  <Col xs={12} className="inline_container add-padding-top-5 add-padding-left-8">
                    <ThumbnailsCrop
                      setModalOpen={thumbnailSetting === 'crop' ? setThumbnailCropModalOpen : setThumbnailUploadModalOpen}
                      isGgGolfHub={props.isGgGolfHub}
                      hideEditButton={!props.isGgGolfHub && thumbnailSetting === 'upload'}
                    />
                    {thumbnailSetting === 'crop' && (() => {
                        const courseAlbumForCrop = (!props.isGgGolfHub && setting === 'template') || _.isEmpty(props.newRecordsUpdated) || !props.newRecords[0]
                          ? selectedCourseAlbum
                          : {photos: [ props.newRecordsUpdated[props.newRecords[0].image] ]}
                        return courseAlbumForCrop?.photos?.[0] && (
                          <ThumbnailsCropPopup
                            modalOpen={thumbnailCropModalOpen}
                            setModalOpen={setThumbnailCropModalOpen}
                            selectedCourseAlbum={courseAlbumForCrop}
                            newThumbnail={newThumbnail}
                            setNewThumbnail={setNewSelectedThumbnail}
                          />
                        )
                      })()}
                    {thumbnailSetting === 'upload' && (
                      <ThumbnailsUploadPopup
                        modalOpen={thumbnailUploadModalOpen}
                        setModalOpen={setThumbnailUploadModalOpen}
                        setNewThumbnail={setNewSelectedThumbnail}
                      />
                    )}
                  </Col>
                </Col>
                {!props.isGgGolfHub && <Col xs={12} sm={3} md={7} lg={12} className="inline_container add-padding-top-5 add-padding-left-8">
                <GGChipGroup
                  name='thumbnail'
                  options={cropChipOptions}
                  value={thumbnailSetting}
                  onChange={handleOnChangeCropChips}
                />
              </Col>}
              <Col xs={12} sm={3} md={7} lg={12} className="inline_container add-padding-top-10 add-padding-left-8">
                <div className='text-ds-form-label'>
                  {window.I18n.t('views.golfhub.thumbnails_upload.thumbnail_preview')}
                </div>
                <IframeContainerThumbnail
                  thumbnail={thumb}
                  data={previewData}
                />
                {thumbnailSetting === 'upload' && (
                  <div className='add-margin-top-5'>
                    <ThumbnailsUpload
                      setModalOpen={setThumbnailUploadModalOpen}
                    />
                  </div>
                )}
              </Col>
            </Col>
          </Col>
        </div>
      }
    </Fragment>
  )
})

GolfHubRoot.propTypes = {
  schedules: PropTypes.array,
  sponsors: PropTypes.array,
  courseAlbums: PropTypes.array,
  leagueId: PropTypes.string,
  setup: PropTypes.object,
  isCharityOuting: PropTypes.string,
  courseDetailsPath: PropTypes.string,
  tabularViewPath: PropTypes.string,
  thumbnail: PropTypes.object,
  eventCardPreviewData: PropTypes.object,
  isGgGolfHub: PropTypes.bool,
  twoStepFlow: PropTypes.bool,
}

TemplateFields.propTypes = {
  label: PropTypes.string,
  leftLabel: PropTypes.string,
  items: PropTypes.array,
  defaultValue: PropTypes.string,
  inputName: PropTypes.string,
  newRecords: PropTypes.array,
  setNewRecords: PropTypes.func,
  newRecordsUpdated: PropTypes.object,
  deleted: PropTypes.array,
  setDeleted: PropTypes.func,
  setModalOpen: PropTypes.func,
  thumbnail: PropTypes.object,
  eventCardPreviewData: PropTypes.object,
  thumbnail_setting: PropTypes.string,
  leagueId: PropTypes.string,
  mainSetNewThumbnail: PropTypes.func,
  mainSetThumbnailSetting: PropTypes.func,
  timeFormat: PropTypes.string,
  isGgGolfHub: PropTypes.bool,
}

export default GolfHubRoot
